"use client";

// recharts dựng trên React context + DOM nên chỉ chạy được ở client.
// Thiếu directive này thì mọi server component import barrel @chotca/ui đều nổ
// "createContext is not a function" khi Next đánh giá module trên server.
import * as React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard } from "./chart-card";
import {
  CHART_AXIS_TICK,
  CHART_GRID_STROKE,
  CHART_TOOLTIP_STYLE,
} from "./chart-theme";

export interface ChartSeries {
  /** Key into each data row. */
  key: string;
  /** Legend / tooltip label. */
  label: string;
  /** Stroke (and fill, when `area`) color. */
  color: string;
}

export interface LineChartCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  data: Array<Record<string, unknown>>;
  /** Field holding the x value (usually the day key). */
  xKey: string;
  series: ChartSeries[];
  height?: number;
  /** Render filled areas instead of plain lines. */
  area?: boolean;
  /** Format x tick + tooltip label (e.g. day key -> "18/06"). */
  xTickFormatter?: (value: string) => string;
  /** Format y tick + tooltip value. */
  valueFormatter?: (value: number) => string;
}

/** Multi-series line (or area) chart wrapped in the shared ChartCard. */
export function LineChartCard({
  title,
  subtitle,
  action,
  data,
  xKey,
  series,
  height = 240,
  area = false,
  xTickFormatter,
  valueFormatter,
}: LineChartCardProps) {
  const Chart = area ? AreaChart : LineChart;
  return (
    <ChartCard title={title} subtitle={subtitle} action={action}>
      <ResponsiveContainer width="100%" height={height}>
        <Chart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_GRID_STROKE}
            vertical={false}
          />
          <XAxis
            dataKey={xKey}
            tick={CHART_AXIS_TICK}
            tickFormatter={xTickFormatter}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickFormatter={
              valueFormatter ? (v: number) => valueFormatter(v) : undefined
            }
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelFormatter={(label: unknown) =>
              xTickFormatter ? xTickFormatter(String(label)) : String(label)
            }
            formatter={(value: unknown) =>
              valueFormatter ? valueFormatter(Number(value)) : String(value)
            }
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s) =>
            area ? (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.12}
                strokeWidth={2}
              />
            ) : (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ),
          )}
        </Chart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
